import { OuiSymbolComponent } from '@aiz-gl/omni-ui';
import { Component, computed, inject, signal } from '@angular/core';
import { TEXT } from '../../constants/text';
import { AuthService } from '../../services/auth/auth.service';
import { ToastService } from '../../services/toast/toast.service';
import { UserDccsService } from '../../services/user-dccs/user-dccs.service';

@Component({
  selector: 'app-user-profile',
  imports: [OuiSymbolComponent],
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss',
})
export class UserProfileComponent {
  auth = inject(AuthService);
  private readonly userDccsService = inject(UserDccsService);
  private readonly toast = inject(ToastService);

  protected readonly T = TEXT;

  protected readonly initials = computed(() => {
    const name = this.auth.userName() || this.auth.userEmail() || '';
    if (!name) return '?';
    const parts = name
      .replace(/@.*/, '')
      .replace(/[._-]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  });

  protected readonly roles = computed(
    () => this.auth.authProfile()?.roles ?? [],
  );
  protected readonly businessEntities = computed(
    () => this.auth.authProfile()?.businessEntities ?? [],
  );

  /** DCCs assigned to the user (loaded at login). */
  protected readonly dccs = computed(
    () => this.userDccsService.userAllocatedDccs()?.data ?? [],
  );

  /** Explicit user pick; null until they change the dropdown. */
  private readonly chosenDcc = signal<string | null>(null);

  /**
   * The currently-selected DCC code: the user's explicit pick, else the one
   * flagged `is_default` from the API. Null when no preference is saved (and
   * none picked yet) — the dropdown then shows the "Select a DCC" placeholder.
   */
  protected readonly selectedDcc = computed<string | null>(() => {
    const explicit = this.chosenDcc();
    if (explicit) return explicit;
    return this.dccs().find((d) => d.is_default)?.dcc_code ?? null;
  });

  /** True while a save request is in flight. */
  protected readonly saving = signal(false);

  onSelectDcc(code: string): void {
    this.chosenDcc.set(code);
  }

  onSavePreferences(): void {
    const code = this.selectedDcc();
    const email = this.auth.userEmail();
    if (!code || !email || this.saving()) return;

    this.saving.set(true);
    this.userDccsService
      .saveUserPreferences({ email, default_dcc: code })
      .subscribe({
        next: (res) => {
          if (res?.success) {
            // Reflect the new default locally (cache + active DCC) so the
            // dashboard landing and this dropdown stay in sync without a reload.
            this.userDccsService.markDefaultDcc(code);
            this.userDccsService.setUserAssignedDcc(code);
            this.chosenDcc.set(null);
            this.toast.success(this.T.userProfile.preferences.saved);
          } else {
            this.toast.error(
              res?.errors?.[0] ?? this.T.userProfile.preferences.saveFailed,
            );
          }
          this.saving.set(false);
        },
        error: (err) => {
          this.toast.error(
            err?.error?.errors?.[0] ??
              err?.message ??
              this.T.userProfile.preferences.saveFailed,
          );
          this.saving.set(false);
        },
      });
  }
}
